import crossFetch from 'cross-fetch';
import fetchRetry from 'fetch-retry';
import isEmpty from 'lodash-es/isEmpty';
import { isHydrated } from '../utils/utils';
import type { SharedProperties } from '../types/helpers';

const UTILS_FETCH_RETRY_CONFIG: Parameters<typeof fetchRetry>[1] = {
    retries: 3,
    retryOn: [403, 429, 502, 503, 504, 505, 506, 507, 508, 509, 510, 511],
    retryDelay: (attempt: number) => 2 ** attempt * 600, // 600, 1200, 2400
};

const fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> =>
    isHydrated() && typeof window.fetch === 'function'
        ? window.pwaAuthFlowPromise.then(() =>
              fetchRetry(window.fetch, UTILS_FETCH_RETRY_CONFIG)(input, init),
          )
        : fetchRetry(crossFetch, UTILS_FETCH_RETRY_CONFIG)(input, init);

const handleFetchJSONResponse = <ResponseType>(response: Response): PromiseLike<ResponseType> =>
    response.json().then((json: ResponseType) => {
        if (response.status >= 200 && response.status < 300) {
            return json;
        }
        const err = new Error('Request error');
        throw err;
    });

export const postJson = <RequestType, ResponseType>(
    url: string,
    data: RequestType,
): Promise<ResponseType> => {
    let json: BodyInit | null = null;
    if (typeof data === 'object') {
        json = JSON.stringify(data);
    } else if (typeof data !== 'string') {
        throw new Error('Data must be an object or a JSON string.');
    }
    return window.pwaAuthFlowPromise.then(() =>
        fetch(url, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
            body: json,
        }).then(
            (response: Response): PromiseLike<ResponseType> => handleFetchJSONResponse(response),
        ),
    );
};

export const getJson = <ResponseType>(url: string): Promise<ResponseType> =>
    window.pwaAuthFlowPromise.then(() =>
        fetch(url, {
            credentials: 'include',
            headers: {
                Accept: 'application/json',
            },
        }).then(
            (response: Response): PromiseLike<ResponseType> => handleFetchJSONResponse(response),
        ),
    );

export const redirectWithPost = (url: string, values: Record<string, string>): void => {
    if (!isHydrated()) {
        return;
    }

    const form = document.createElement('form');
    form.style.visibility = 'hidden';
    form.method = 'POST';
    form.action = url;

    document.body.appendChild(form);

    if (!isEmpty(values)) {
        Object.entries(values).forEach(([name, value]) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value;
            form.appendChild(input);
        });
    }

    form.submit();
};

/**
 * Merges two arrays of objects by a common key
 * @param a first array, which contains the items that will be preserved
 * @param b second array to merge onto the first
 * @param key key that is common to both arrays
 * @returns The result is an array of objects with the properties of both arrays.
 */
export function leftMergeArrays<
    T extends V,
    U extends V,
    P extends keyof V,
    V extends SharedProperties<T, U>,
>(a: T[], b: U[], key: P): (T & Partial<U>)[] {
    const items = new Map<V[P], T & Partial<U>>(
        // FIXME: Can't figure out the type for now, it is taking too long to get it right.
        // @ts-expect-error: Type 'T | (T & U)' is not assignable to type 'T & Partial<U>'.
        a.map((i) => [i[key], i]) ?? [],
    );

    b.forEach((i) => {
        const item = items.get(i[key]);
        if (item) {
            items.set(i[key], { ...item, ...i });
        }
    });

    return Array.from(items.values());
}
